import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";

import { AiType } from "./HeroComponents";
import Button from "../library/Button";
import ContactForm from "./ContactForm";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

// Loading placeholder component that mimics AskUserInput
const LoadingInputPlaceholder = () => {
  return (
    <div
      className={clsx(
        "rounded-lg",
        "relative",
        "!mt-8 ml-6",
        "tracking-[0.03em]",
        "!p-0",
        "!bg-transparent",
        "!shadow-none",
        "w-full",
        "h-full",
        "flex",
        "font-sans"
      )}
    >
      <div className="relative w-full">
        <textarea
          placeholder="How can I help?"
          className="w-full p-2 pr-16 rounded-lg focus:ring-gailPrimary text-sm resize-none min-h-[42px] placeholder:text-gray-500 !bg-transparent font-semibold !border-0 !ring-0 focus:!ring-0 focus:!outline-none"
          rows={1}
        />
        <div className="absolute top-1 right-2">
          <div className="flex flex-row gap-1"></div>
        </div>
      </div>
    </div>
  );
};

interface HeroAssistantsProps {
  aiAssistants: AiType[];
  assistant: "insurance" | "banking" | "finance";
  setAssistant: Dispatch<SetStateAction<"insurance" | "banking" | "finance">>;
  selectedSuggestion: string | null;
  setSelectedSuggestion: Dispatch<SetStateAction<string | null>>;
}

const HeroAssistants = ({
  aiAssistants,
  assistant,
  setAssistant,
  selectedSuggestion,
  setSelectedSuggestion,
}: HeroAssistantsProps) => {
  const { t } = useTranslation();
  // Add a key prop to force re-render of ContactForm when selectedSuggestion changes
  const formKey = selectedSuggestion || "no-suggestion";
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  // Get appropriate URL based on selected assistant
  const getAssistantUrl = () => {
    let baseUrl = "";
    switch (assistant) {
      case "insurance":
        baseUrl = process.env.NEXT_PUBLIC_AI_URL_INSURANCE || "";
        break;
      case "banking":
        baseUrl = process.env.NEXT_PUBLIC_AI_URL_BANKING || "";
        break;
      case "finance":
        baseUrl = process.env.NEXT_PUBLIC_AI_URL_FINANCE || "";
        break;
      default:
        baseUrl = process.env.NEXT_PUBLIC_AI_URL_INSURANCE || "";
    }

    // Add expand=true parameter
    return (
      baseUrl +
      (baseUrl.includes("?")
        ? "&fixed=true&storeMessages=false"
        : "?fixed=true&storeMessages=false")
    );
  };

  // Function to send a message to the iframe
  const sendMessageToIframe = (message: string) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const messageData = {
        type: "applyMessage",
        payload: message,
      };
      iframeRef.current.contentWindow.postMessage(messageData, "*");
    }
  };

  // Effect to handle suggestion changes
  useEffect(() => {
    if (selectedSuggestion && !selectedSuggestion.includes("-suggestion-0")) {
      // Parse the suggestion to get the type and index
      // Format is "{assistant}-suggestion-{index}"
      const parts = selectedSuggestion.split("-");
      if (parts.length >= 3) {
        const suggestionType = parts[0];
        const suggestionIndex = parseInt(parts[parts.length - 1]);

        // Get the actual translated text using the translation key
        const suggestionText = t(
          `hero.tabs.${suggestionType}.suggestions.${suggestionIndex}`
        );

        // Send the actual suggestion text to the iframe
        sendMessageToIframe(suggestionText);
      }
    }
  }, [selectedSuggestion, assistant, t]);

  const handleIframeLoad = () => {
    setIsIframeLoading(false);
  };

  return (
    <div
      className={clsx(
        "bg-white/80",
        "backdrop-blur-xs",
        "rounded-2xl",
        "shadow-xl",
        "w-full",
        "lg:h-full",
        "lg:max-w-lg",
        "flex",
        "flex-col",
        "ml-auto",
        "h-[80%]",
        "lg:max-w-2xl",
        "mx-auto",
        "lg:mr-0"
      )}
    >
      <div className={clsx("flex", "mx-auto")}>
        {aiAssistants.map((aiType, index) => (
          <Fragment key={aiType.key}>
            <Button
              type="none"
              tiny
              className={clsx(
                "rounded-none",
                "font-medium",
                {
                  "text-[#343434] hover:text-black": assistant !== aiType.key,
                },
                {
                  "text-black !font-bold": assistant === aiType.key,
                }
              )}
              onClick={() => {
                setAssistant(aiType.key as "insurance" | "banking" | "finance");
                setSelectedSuggestion(null);
              }}
            >
              {aiType.label}
            </Button>
            {index < aiAssistants.length - 1 && (
              <div className="h-5 w-px bg-gray-300 self-center mx-1" />
            )}
          </Fragment>
        ))}
      </div>
      <hr className={clsx("border-gray-200")} />
      <div className={clsx("flex-grow", "p-4", "relative", "overflow-hidden")}>
        <div
          className={clsx(
            "absolute top-0 left-0 w-full h-full transition-opacity duration-300 p-4",
            "iframe-container",
            selectedSuggestion && selectedSuggestion.includes("-suggestion-0")
              ? "opacity-0 pointer-events-none"
              : "opacity-100"
          )}
          style={{ height: "100%", overflow: "auto" }}
        >
          {isIframeLoading && (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
              <LoadingInputPlaceholder />
            </div>
          )}
          <iframe
            ref={iframeRef}
            width="100%"
            height="100%"
            src={getAssistantUrl()}
            style={{
              border: "none",
              display: "block",
              background: "transparent",
              minHeight: "250px",
              overflow: "auto",
            }}
            onLoad={handleIframeLoad}
          />
        </div>
        <div
          className={clsx(
            "absolute top-0 left-0 w-full h-full transition-opacity duration-300 p-4",
            selectedSuggestion && selectedSuggestion.includes("-suggestion-0")
              ? "opacity-100"
              : "opacity-0 pointer-events-none"
          )}
        >
          {selectedSuggestion &&
            selectedSuggestion.includes("-suggestion-0") && (
              <ContactForm
                key={formKey}
                onReset={() => setSelectedSuggestion(null)}
                assistantType={assistant}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default HeroAssistants;
